// src/pages/LessonStrategyPage.jsx
import LessonContentRenderer from './LessonContentRenderer.jsx';
// --- CHANGE 1: Import useNavigate and new components ---
import { useLocation, useParams } from 'react-router-dom';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import NextLessonButton from '../study/NextLessonButton.jsx'; // Import the new button
import React, { useState, useEffect, useMemo, useRef } from 'react';
import examService from '../../services/examService.js';
import { logLessonCompletion } from '../../utils/activityLogger.js';
import { auth } from '../../firebase.js';
import subscriptionService from '../../services/subscriptionService.js';
import adminService from '../../services/adminService.js';
// ... (keep all other imports)
import {
  Clock, CheckCircle, Target, Lightbulb, BookOpen, ArrowRight, ArrowLeft,
  Play, AlertTriangle, Zap, Users, FileText, Brain, Award,
  ChevronDown, ChevronRight, ChevronLeft, Star, TrendingUp, Headphones,
  PenTool, MessageCircle, Puzzle, Mail, Edit, Mic, Grid, Dumbbell, List
} from 'lucide-react';
import GrammarBottomNav from '../grammar/GrammarBottomNav.jsx';
import { useUserStats } from '../../hooks/useUserStats.js';
import { useGamification } from '../../hooks/useGamification.js';
import MockExamFloatingButton from '../study/MockExamFloatingButton.jsx';
import { getExamInfoFromDataUrl, getMockExamPartId, getSectionIdFromLessonType } from '../../utils/mockExamMapper.js';
import AhaUpsellModal from '../ui/AhaUpsellModal.jsx';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';
import { useTranslation } from 'react-i18next';

// ... (keep all custom hooks: useEnhancedButton, useRevealAnimation, useProgressAnimation) ...
const useEnhancedButton = (initialState = 'idle') => {
  const [currentState, setCurrentState] = useState(initialState);
  const [isHovered, setIsHovered] = useState(false);

  const buttonProps = {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    currentState,
    isHovered
  };

  const executeAction = async (action) => {
    setCurrentState('loading');
    try {
      await action();
      setCurrentState('success');
      setTimeout(() => setCurrentState('idle'), 1200);
    } catch (error) {
      setCurrentState('error');
      setTimeout(() => setCurrentState('idle'), 2000);
    }
  };

  const getAnimationClasses = () => {
    const base = 'transform transition-all duration-300 ease-out';
    const hover = isHovered ? 'scale-105 shadow-lg' : 'scale-100';
    const state = currentState === 'loading' ? 'animate-pulse' : '';
    return `${base} ${hover} ${state}`;
  };

  return { buttonProps, executeAction, getAnimationClasses, currentState };
};

const useRevealAnimation = (delay = 0) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const revealClasses = isVisible
    ? 'opacity-100 translate-y-0'
    : 'opacity-0 translate-y-4';

  return `transform transition-all duration-700 ease-out ${revealClasses}`;
};

const useProgressAnimation = (targetProgress, duration = 2000) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let animationFrameId;
    const startTime = Date.now();

    const animate = () => {
      const elapsedTime = Date.now() - startTime;
      const progressFraction = Math.min(elapsedTime / duration, 1);
      const currentProgress = progressFraction * targetProgress;

      setProgress(currentProgress);

      if (elapsedTime < duration) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrameId);
  }, [targetProgress, duration]);


  return progress;
};

const AccordionItem = ({ block, animationDelay }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  // Safely render Redemittel content
  const createMarkup = (htmlString) => {
    const sanitized = htmlString
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-[#3A86FF]">$1</strong>'); // UPDATED: Use royal blue
    return { __html: sanitized };
  };

  return (
      <div className="animate-fade-in-up my-4" style={{ animationDelay }}>
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-lg">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full p-4 text-left font-semibold text-gray-800 flex justify-between items-center hover:bg-gray-50 transition-colors"
          >
            <span className="flex-1 pr-2">{block.title}</span>
            {isOpen ? <ChevronDown className="w-5 h-5 flex-shrink-0" /> : <ChevronRight className="w-5 h-5 flex-shrink-0" />}
          </button>
          {isOpen && (
            <div className="p-4 border-t border-gray-200 bg-gray-50/50">
              {typeof block.content === 'string' ? (
                <p className="text-gray-600">{block.content}</p>
              ) : (
                <div className="space-y-3">
                  {Array.isArray(block.content) ? block.content.map((item, i) => (
                    <div key={i}>
                      {item.function && <p className="font-semibold text-gray-700">{item.function}</p>}
                      {item.redemittel && <p className="text-gray-600" dangerouslySetInnerHTML={createMarkup(item.redemittel)} />}
                      {item.example && <p className="text-sm text-gray-600 mt-1" dangerouslySetInnerHTML={createMarkup(item.example)} />}
                    </div>
                  )) : (
                    Object.entries(block.content).map(([key, value]) => (
                       <p key={key} className="text-gray-600"><strong className="capitalize font-medium text-gray-700">{key}:</strong> {value}</p>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
  );
};
const LessonStrategyPage = ({ defaultDataUrl = '/data/lessons/telc-b1/lv_part1.json' }) => {
  const location = useLocation();
  const { examSlug } = useParams();
  const navigate = useLocalizedNavigate();
  const { t } = useTranslation();
  const { dataUrl, currentLessonId, studyPlan, lessonStatusMap } = location.state || {};
  const examPrefix = examSlug ? `/exams/${examSlug}` : '';

  const [lessonData, setLessonData] = useState(null);
  const [lessonLoading, setLessonLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [exercisesPanelOpen, setExercisesPanelOpen] = useState(false);
  const [sectionIndex, setSectionIndex] = useState(0);
  const [totalSections, setTotalSections] = useState(1);
  const [isBackHovered, setIsBackHovered] = useState(false);
  const openSectionsMenuRef = useRef(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(subscriptionService.getDefaultFreeStatus());

  const { userStats, loading: userStatsLoading } = useUserStats();
  const { awardXP } = useGamification();
  const [mockExamData, setMockExamData] = useState(null);
  const revealMain = useRevealAnimation(100);
  const revealContent = useRevealAnimation(300);
  const [startTime, setStartTime] = useState(Date.now());
  const [showLessonAhaModal, setShowLessonAhaModal] = useState(false);
  const [pendingLessonNav, setPendingLessonNav] = useState(null);

  const maybeShowLessonAha = (navigateFn) => {
    if (subscriptionStatus?.isFree) {
      setPendingLessonNav(() => navigateFn);
      setShowLessonAhaModal(true);
      return true;
    }
    return false;
  };

  const navigateWithLessonAha = (navigateFn) => {
    if (maybeShowLessonAha(navigateFn)) return;
    navigateFn();
  };

  // --- MODIFIED useEffect to fix immediate button appearance ---
  useEffect(() => {
    const handleScroll = () => {
      // Prevent running the check if the page content isn't loaded
      if (!lessonData) return;

      const threshold = 150;
      const isBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - threshold;
      setIsAtBottom(isBottom);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // **THE FIX**: Check the scroll position after a short delay.
    // This allows the DOM to render and gives us the correct page height.
    const timerId = setTimeout(() => {
        handleScroll();
    }, 800);

    // Cleanup function to remove the listener and clear the timeout
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timerId);
    };
  }, [lessonData]); // The dependency on lessonData is correct and remains.

  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      const user = auth.currentUser;
      if (!user?.uid) {
        setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
        return;
      }
      try {
        const status = await subscriptionService.getSubscriptionStatus(user.uid);
        setSubscriptionStatus(status);
      } catch (error) {
        console.error('Error loading subscription status in lesson strategy:', error);
        setSubscriptionStatus(subscriptionService.getDefaultFreeStatus());
      }
    };

    loadSubscriptionStatus();
  }, []);

  // Keep lesson access rules consistent with StudyPlanPage to prevent premium bypass from in-lesson navigation.
  const isModuleLocked = (moduleId) => {
    if (moduleId === 'module_01_intro') return false;
    if (adminService.isAdmin()) return false;
    return subscriptionStatus?.isFree || false;
  };

  const isLesenPartOneLesson = (lesson, moduleTitle, lessonIndex) => {
    const moduleId = String(lesson?.__moduleId || '').toLowerCase();
    const normalizedModuleTitle = String(moduleTitle || '').toLowerCase();
    const lessonId = String(lesson?.lessonId || '').toLowerCase();
    const lessonTitle = String(lesson?.title || '').toLowerCase();

    const isReadingModule =
      moduleId.includes('lesen') ||
      moduleId.includes('reading') ||
      normalizedModuleTitle.includes('lese') ||
      normalizedModuleTitle.includes('reading');

    if (!isReadingModule) return false;

    const partOnePattern = /(teil\s*1|part\s*1)/i;
    const idPartOnePattern = /(part[_-]?1|teil[_-]?1|t1)/;
    const isReadingLesson =
      /(lv[_-]?|lesen[_-]?|reading[_-]?)/.test(lessonId) ||
      /(lese|reading)/i.test(lessonTitle);

    return isReadingLesson && (idPartOnePattern.test(lessonId) || partOnePattern.test(lessonTitle) || lessonIndex === 0);
  };

  const isLessonFreePreview = (lesson) => {
    if (lesson?.__moduleId === 'module_01_intro') return true;
    return isLesenPartOneLesson(lesson, lesson?.__moduleTitle, lesson?.__lessonIndex ?? 0);
  };

  const isLessonLocked = (lesson) => {
    if (!lesson) return false;
    return isModuleLocked(lesson.__moduleId) && !isLessonFreePreview(lesson);
  };

  const allLessonEntries = useMemo(() => {
    if (!studyPlan?.modules) return [];
    return studyPlan.modules.flatMap((module) =>
      (module.lessons || []).map((lesson, lessonIndex) => ({
        ...lesson,
        __moduleId: module.moduleId,
        __moduleTitle: module.title,
        __lessonIndex: lessonIndex,
      }))
    );
  }, [studyPlan]);

  const nextLesson = useMemo(() => {
    if (!currentLessonId || !lessonStatusMap || allLessonEntries.length === 0) return null;

    const currentIndex = allLessonEntries.findIndex((l) => l.lessonId === currentLessonId);

    if (currentIndex === -1) return null;

    for (let i = currentIndex + 1; i < allLessonEntries.length; i++) {
      const lesson = allLessonEntries[i];
      if (lessonStatusMap[lesson.lessonId] !== 'coming_soon' && !isLessonLocked(lesson)) {
        return lesson;
      }
    }

    return null;
  }, [currentLessonId, lessonStatusMap, allLessonEntries, subscriptionStatus]);

  const prevLesson = useMemo(() => {
    if (!currentLessonId || !lessonStatusMap || allLessonEntries.length === 0) return null;

    const currentIndex = allLessonEntries.findIndex((l) => l.lessonId === currentLessonId);

    if (currentIndex <= 0) return null;

    for (let i = currentIndex - 1; i >= 0; i--) {
      const lesson = allLessonEntries[i];
      if (lessonStatusMap[lesson.lessonId] !== 'coming_soon' && !isLessonLocked(lesson)) {
        return lesson;
      }
    }

    return null;
  }, [currentLessonId, lessonStatusMap, allLessonEntries, subscriptionStatus]);


  const totalSteps = useMemo(() => {
    if (!lessonData) return 0;
    return lessonData.contentBlocks?.reduce((acc, block) => {
      if (block.type === 'exam_section') {
        return acc + (block.sub_parts?.length || 0);
      }
      return acc;
    }, 0) || 0;
  }, [lessonData]);

  const completionPercentage = totalSteps > 0 ? Math.round((completedSteps.size / totalSteps) * 100) : 0;
  const progressValue = useProgressAnimation(completionPercentage, 2000);

  // Tab title from study plan (e.g. "Aufbau der TELC B1 Prüfung"), fallback to content JSON title
  const tabTitle = (() => {
    if (studyPlan && currentLessonId) {
      const lesson = studyPlan.modules?.flatMap((m) => m.lessons || []).find((l) => l.lessonId === currentLessonId);
      if (lesson?.title) return lesson.title;
    }
    return lessonData?.title || null;
  })();

  useEffect(() => {
    if (tabTitle) {
      const prev = document.title;
      document.title = `${tabTitle} | Logami`;
      return () => { document.title = prev; };
    }
  }, [tabTitle]);

  useEffect(() => {
    const loadLessonData = async () => {
      const urlToFetch = dataUrl || defaultDataUrl;
      if (!urlToFetch) {
        setLessonLoading(false);
        return;
      }
      setLessonLoading(true);
      try {
        const response = await fetch(urlToFetch);
        const data = await response.json();
        setLessonData(data);
        const examInfo = getExamInfoFromDataUrl(urlToFetch);
        if (examInfo) {
          await loadMockExamData(examInfo, examInfo.lessonType);
        }
      } catch (error) {
        console.error('Error loading lesson data:', error);
        setLessonData(null);
      } finally {
        setLessonLoading(false);
      }
    };

    loadLessonData();
    window.scrollTo(0, 0);
    setIsAtBottom(false);
  }, [dataUrl, defaultDataUrl]);

  const handleNavigateNext = async () => {
    if (!nextLesson) return;

    // --- ADD THE LOGGING LOGIC HERE ---
    if (lessonData) {
      const timeSpentInSeconds = Math.round((Date.now() - startTime) / 1000);
      const skill = lessonData.lessonId.split('-')[1] || 'general'; // Extracts 'lv', 'hv', etc.

      await logLessonCompletion({
        lessonId: lessonData.lessonId,
        title: lessonData.title,
        timeSpent: timeSpentInSeconds,
        score: completedSteps.size, // Number of steps completed
        maxScore: totalSteps, // Total steps available
        completed: true,
        correctAnswers: completedSteps.size,
        totalQuestions: totalSteps,
        skills: [skill],
        topics: [lessonData.title],
        level: lessonData.lessonId.split('-')[0] // Extracts 'telc-b1', etc.
      });
    }
    const strategyPageTypes = ['theory_practice', 'speaking_practice', 'writing_practice', 'strategy'];
    if (nextLesson.dataUrl || strategyPageTypes.includes(nextLesson.type)) {
      navigate(examPrefix ? `${examPrefix}/lesson-strategy` : '/lesson-strategy', {
        state: {
          dataUrl: nextLesson.dataUrl,
          currentLessonId: nextLesson.lessonId,
          studyPlan: studyPlan,
          lessonStatusMap: lessonStatusMap,
        },
      });
      return;
    }

    switch (nextLesson.type) {
      case 'vocabulary_trainer_link':
        navigateWithLessonAha(() => navigate(examPrefix ? `${examPrefix}/practice/vocabulary` : '/practice/vocabulary'));
        break;
      case 'exam_structure':
         const { provider, level } = getExamInfoFromDataUrl(dataUrl);
         navigateWithLessonAha(() => navigate(`/exam-structure/${provider.toLowerCase()}/${level.toLowerCase()}`));
         break;
      default:
        goToLessons();
        break;
    }
  };

  const handleNavigatePrev = () => {
    if (!prevLesson) {
      goToLessons();
      return;
    }
    const strategyPageTypes = ['theory_practice', 'speaking_practice', 'writing_practice', 'strategy'];
    if (prevLesson.dataUrl || strategyPageTypes.includes(prevLesson.type)) {
      navigate(examPrefix ? `${examPrefix}/lesson-strategy` : '/lesson-strategy', {
        state: {
          dataUrl: prevLesson.dataUrl,
          currentLessonId: prevLesson.lessonId,
          studyPlan: studyPlan,
          lessonStatusMap: lessonStatusMap,
        },
      });
      return;
    }
    switch (prevLesson.type) {
      case 'exam_structure':
        const { provider, level } = getExamInfoFromDataUrl(prevLesson.dataUrl || dataUrl);
        navigateWithLessonAha(() => navigate(`/exam-structure/${provider?.toLowerCase() || 'telc'}/${level?.toLowerCase() || 'b1'}`));
        break;
      default:
        goToLessons();
        break;
    }
  };

  const goToLessons = (highlightLessonId = currentLessonId) => {
    const navigateToLessons = () => {
      navigate(examPrefix ? `${examPrefix}/lessons` : '/lessons', {
        state: highlightLessonId ? { openLessonId: highlightLessonId } : undefined,
      });
    };

    navigateWithLessonAha(navigateToLessons);
  };

  const handleLessonAhaDismiss = () => {
    setShowLessonAhaModal(false);
    if (typeof pendingLessonNav === 'function') {
      pendingLessonNav();
    }
    setPendingLessonNav(null);
  };

  const handleLessonAhaUpgrade = () => {
    setShowLessonAhaModal(false);
    setPendingLessonNav(null);
  };

  useEffect(() => {
    if (userStats && lessonData?.lessonId) {
      const lessonProgress = userStats.progress?.[lessonData.lessonId]?.completedSteps;
      if (lessonProgress && Array.isArray(lessonProgress)) {
        setCompletedSteps(new Set(lessonProgress));
      }
    }
  }, [userStats, lessonData]);

  const loadMockExamData = async (examInfo, lessonType) => {
  try {

    const partId = getMockExamPartId(lessonType, examInfo.provider);
    const sectionId = getSectionIdFromLessonType(lessonType);

    if (!partId || !sectionId) {
      console.log('Invalid lesson type or provider for mock exam:', lessonType, examInfo.provider);
      return;
    }

    // Use examService to get available exams dynamically
    const availableExams = examService.getAvailableExams();
    const providerKey = examInfo.provider.toLowerCase();
    const levelKey = examInfo.level.toUpperCase();

    // Check if this provider/level combination exists
    const examLevel = availableExams[providerKey]?.[levelKey];
    if (!examLevel || examLevel.length === 0) {
      console.log(`No mock exams available for ${examInfo.provider} ${examInfo.level}`);
      return;
    }

    // Try to get the original/default exam file — the original is the entry
    // whose file has no " - <variant>" suffix (display names are city names
    // now, so matching on the name is not reliable).
    const originalExam = examLevel.find(exam => exam.file && !exam.file.includes(' - '))
      || examLevel.find(exam =>
        ((exam.legacyName || exam.name) || '').toLowerCase().includes('original') ||
        ((exam.legacyName || exam.name) || '').toLowerCase().includes('übungstest')
      ) || examLevel[0]; // Fallback to first available

    if (!originalExam) {
      console.log('No suitable mock exam found');
      return;
    }

    // Construct the file path using the examService pattern
    const mockExamFile = originalExam.file;
    const response = await fetch(`/data/exams/${providerKey}/${levelKey.toLowerCase()}/${mockExamFile}`);

    if (!response.ok) {
      console.log(`Mock exam file not found: ${mockExamFile}`);
      return;
    }

    const examData = await response.json();

    // Apply provider-specific transformations if needed
    let processedExamData = examData;
    if (examData.examProvider === 'TELC') {
      processedExamData = examService.applyTelcTransformation(examData);
    } else if (examData.examProvider === 'OSD') {
      processedExamData = examService.applyOsdTransformation(examData);
    }

    const section = processedExamData.sections.find(s => s.sectionId === sectionId);

    if (section) {
      const part = section.parts.find(p => p.partId === partId);
      if (part) {
        setMockExamData({
          ...part,
          examProvider: processedExamData.examProvider,
          sectionName: section.sectionName,
          examFile: mockExamFile // Store for debugging
          // Remove availableExams count to hide variant information
        });
        console.log(`Successfully loaded mock exam: ${examInfo.provider} ${examInfo.level} - ${part.partName}`);
      } else {
        console.log(`Mock exam part '${partId}' not found in section '${sectionId}'`);
      }
    } else {
      console.log(`Mock exam section '${sectionId}' not found`);
    }
  } catch (error) {
    console.error('Error loading mock exam data:', error);
    // Set mockExamData to null to hide the floating button
    setMockExamData(null);
  }
};
  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const markStepComplete = async (stepId) => {
    const newCompleted = new Set(completedSteps);
    const lessonCategory = lessonData?.lessonId?.split('-')[0] || 'general';

    if (newCompleted.has(stepId)) {
      newCompleted.delete(stepId);
    } else {
      newCompleted.add(stepId);
      try {
        await awardXP(10, lessonCategory);
      } catch (error) {
        console.error("Failed to award XP:", error);
        newCompleted.delete(stepId);
      }
    }
    setCompletedSteps(newCompleted);
  };

  // UPDATED: This function now just returns 'bg-black' to match the theme.
  const getExamTypeColor = (lessonId) => {
    return 'bg-black';
  };

  const getSkillIcon = (lessonId) => {
    if (lessonId?.includes('lesen') || lessonId?.includes('lv')) {
      return BookOpen;
    } else if (lessonId?.includes('hoeren') || lessonId?.includes('hv')) {
      return Headphones;
    } else if (lessonId?.includes('schreiben') || lessonId?.includes('sa')) {
      return PenTool;
    } else if (lessonId?.includes('sprechen') || lessonId?.includes('ma')) {
      return MessageCircle;
    } else if (lessonId?.includes('sprachbausteine') || lessonId?.includes('sb')) {
      return Puzzle;
    }
    return Target;
  };

  const getSectionIcon = (title) => {
    if (title.toLowerCase().includes('step') || title.toLowerCase().includes('schritt')) {
      return Target;
    } else if (title.toLowerCase().includes('strategy') || title.toLowerCase().includes('strategie')) {
      return Brain;
    } else if (title.toLowerCase().includes('drill') || title.toLowerCase().includes('übung')) {
      return Zap;
    }
    return BookOpen;
  };


  const isLoading = lessonLoading || userStatsLoading;
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <LogamiLogoSpinner size="2xl" className="mx-auto" decorative={false} alt="Logami loading" />
          <p className="mt-6 text-black/70 text-lg">{t('lesson.loader.loading')}</p>
        </div>
      </div>
    );
  }

  if (!lessonData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center bg-white p-8 rounded-2xl shadow-xl border-2 border-black/10">
          <AlertTriangle className="w-16 h-16 text-black mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-black">{t('lesson.error.title')}</h2>
          <p className="text-black/70 text-lg mt-2">{t('lesson.error.description')}</p>
          <p className="text-sm text-black/60 mt-2">{t('lesson.error.instruction')}</p>
            <button
              onClick={() => goToLessons()}
              className="mt-6 bg-black text-white font-bold py-2 px-5 rounded-lg hover:bg-gray-800 transition-colors"
            >
              {t('lesson.error.backButton')}</button>
        </div>
      </div>
    );
  }

  const SkillIcon = getSkillIcon(lessonData.lessonId);

  return (
    <div className="min-h-screen bg-white pb-24 lesson-strategy-root">
      {/* Header, compact on mobile (safe-area, smaller type), unchanged on desktop */}
      <div className={`relative bg-black text-white overflow-hidden lesson-strategy-header py-4 sm:py-8 md:py-10 ${revealMain}`}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute inset-0 opacity-[0.05]" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-3 sm:px-6">
          {/* Top row: Back (left), same style as exam/practice */}
          <div className="flex items-center justify-between mb-4 sm:mb-5">
            <button
              type="button"
              onClick={() => goToLessons()}
              onMouseEnter={() => setIsBackHovered(true)}
              onMouseLeave={() => setIsBackHovered(false)}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 min-h-[48px] min-w-[48px] sm:min-h-0 sm:min-w-0 bg-white rounded-lg border-2 border-black relative overflow-hidden transition-all duration-300 hover:shadow-md flex-shrink-0 touch-manipulation"
              aria-label={t('grammar.back', { defaultValue: 'Back' })}
            >
              <div style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: isBackHovered ? '100%' : '0%', background: 'linear-gradient(to left, #9ca3af, #4b5563, #000000)', transition: 'width 0.7s ease-out', zIndex: 0 }} />
              <ArrowLeft className="w-4 h-4 relative z-10 transition-colors duration-500" style={{ color: isBackHovered ? '#ffffff' : '#000000' }} />
              <span className="text-sm font-semibold relative z-10 transition-colors duration-500 sm:inline" style={{ color: isBackHovered ? '#ffffff' : '#000000' }}>
                {t('grammar.back', { defaultValue: 'Back' })}
              </span>
            </button>
            <div className="flex-1 sm:hidden" aria-hidden />
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-2 sm:mb-5">
              <div className="p-2 sm:p-3 bg-white/20 border border-white/30 rounded-xl sm:rounded-2xl backdrop-blur-sm">
                <SkillIcon className="w-5 h-5 sm:w-8 sm:h-8" />
              </div>
            </div>
            <h1 className="text-lg leading-snug font-space font-black tracking-tight mb-1.5 sm:mb-3 sm:text-2xl md:text-4xl lesson-strategy-title">
              {lessonData.title}
            </h1>
            <p className="text-xs opacity-90 max-w-3xl mx-auto sm:text-base md:text-lg font-space lesson-strategy-subtitle">
              {t('lesson.hero.subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-3 px-3 sm:px-6 lg:px-8 sm:py-8 pb-24 sm:pb-8 lesson-strategy-content">
  <LessonContentRenderer
  contentBlocks={lessonData.contentBlocks}
  controlledSection={sectionIndex}
  onSectionChange={(idx, total) => {
    setSectionIndex(idx);
    setTotalSections(total);
  }}
  onOpenSectionsMenuRef={openSectionsMenuRef}
  onComplete={async () => {
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    const lessonId = lessonData?.lessonId || currentLessonId;
    const skill = lessonId?.split('-')[1] || 'general';
    const level = lessonId?.split('-')[0] || 'beginner';
    await logLessonCompletion({
      lessonId,
      title: lessonData?.title ?? 'Lesson',
      timeSpent,
      score: completedSteps?.size ?? 0,
      maxScore: totalSteps ?? 0,
      correctAnswers: completedSteps?.size ?? 0,
      totalQuestions: totalSteps ?? 0,
      skills: [skill],
      topics: lessonData?.title ? [lessonData.title] : [],
      level,
    });
    if (awardXP) {
      awardXP(50, 'lesson_completion');
    }
    goToLessons();
  }}
/>
</div>

      {/* Colorful Next button, hidden on mobile; use bottom nav instead */}
      {isAtBottom && (
          <div className="hidden sm:block">
            {nextLesson ? (
              <NextLessonButton nextLesson={nextLesson} onNavigate={handleNavigateNext} />
            ) : subscriptionStatus?.isFree ? (
              <div className="fixed bottom-0 right-0 p-4 sm:p-6 z-50 animate-slide-up">
                <button
                  onClick={() => setShowLessonAhaModal(true)}
                  className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white font-bold py-3 px-6 rounded-xl shadow-2xl flex items-center justify-center gap-3 transition-all duration-300 ease-in-out hover:scale-105"
                >
                  <span>Unlock Next Lesson</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" /></svg>
                </button>
              </div>
            ) : null}
          </div>
      )}

      <MockExamFloatingButton
          mockExamData={mockExamData}
          examTypeColor={getExamTypeColor(lessonData?.lessonId)}
          open={exercisesPanelOpen}
          onClose={() => setExercisesPanelOpen(false)}
          onOpen={() => setExercisesPanelOpen(true)}
          hideFloatingOnMobile
      />

      {/* Bottom Navigation. Previous | Sections | Lessons | Exercises | Next */}
      <GrammarBottomNav
          buttons={[
            {
              id: 'prev',
              icon: ChevronLeft,
              label: t('nav.previous', { defaultValue: 'Previous' }),
              onClick: () => setSectionIndex((i) => Math.max(0, i - 1)),
              disabled: sectionIndex <= 0,
              color: '#3A86FF',
            },
            {
              id: 'sections',
              icon: Grid,
              label: t('grammar.sections', { defaultValue: 'Sections' }),
              onClick: () => openSectionsMenuRef.current?.(),
              color: '#3A86FF',
            },
            {
              id: 'lessons',
              icon: List,
              label: t('nav.lessons', { defaultValue: 'Lessons' }),
              onClick: () => goToLessons(),
              color: '#3A86FF',
            },
            {
              id: 'exercises',
              icon: Dumbbell,
              label: t('lesson.exercises', { defaultValue: 'Exercises' }),
              onClick: () => mockExamData && setExercisesPanelOpen(true),
              disabled: !mockExamData,
              active: !!mockExamData,
              color: '#006D77',
            },
            {
              id: 'next',
              icon: ChevronRight,
              label: t('nav.next', { defaultValue: 'Next' }),
              onClick: () => setSectionIndex((i) => Math.min(totalSections - 1, i + 1)),
              disabled: totalSections <= 1 || sectionIndex >= totalSections - 1,
              color: '#3A86FF',
            },
          ]}
      />

      <AhaUpsellModal
        open={showLessonAhaModal}
        onDismiss={handleLessonAhaDismiss}
        onUpgrade={handleLessonAhaUpgrade}
        context="lesson"
      />
    </div>
  );
};

export default LessonStrategyPage;